<template>
  <div class="tabs-component">
    <ul role="tablist" class="tabs-component-tabs">
      <li
        v-for="(tab, i) in tabs"
        :key="i"
        :class="{ 'is-active': tab.isActive, 'is-disabled': tab.isDisabled }"
        class="tabs-component-tab"
        role="presentation"
        v-show="tab.isVisible"
      >
        <a
          v-html="tab.header"
          :aria-controls="tab.hash"
          :aria-selected="tab.isActive"
          @click="selectTab(tab.hash, $event)"
          :href="tab.hash"
          class="tabs-component-tab-a"
          role="tab"
        ></a>
      </li>
    </ul>
    <div class="tabs-component-panels">
      <slot />
    </div>
  </div>
</template>

<script>
import expiringStorage from 'vue-tabs-component/src/expiringStorage';

// Vue 3 compat shim for vue-tabs-component.
//
// Origin:
// - Adapted from `node_modules/vue-tabs-component/src/components/Tabs.vue`.
// - We still import `expiringStorage` from the same package internals below.
//
// Why this exists:
// - Before migration, `<tabs>` came directly from the package and lived in node_modules.
// - In Vue 3 compat mode, the original Tabs implementation relies on Vue 2-style
//   child instance access (`$children`) during setup, which is brittle and can
//   break tab discovery/rendering.
// - `vue3-tabs-component` is a Vue 3 fork of this package, but this shim keeps
//   current behaviour with minimal drift during migration; we can switch later.
//
// Migration approach:
// - Vendor Tabs logic here and make child collection Vue 3-safe (`collectTabs`).
// - Keep `<tab>` from the package in `main.js` so existing templates keep working.
const collectTabs = (vnodes, tabs = []) => {
  if (!vnodes) return tabs;
  vnodes.forEach((vnode) => {
    if (!vnode) return;
    if (Array.isArray(vnode)) {
      collectTabs(vnode, tabs);
      return;
    }
    if (vnode.component && vnode.component.proxy) {
      tabs.push(vnode.component.proxy);
    }
    if (Array.isArray(vnode.children)) {
      collectTabs(vnode.children, tabs);
    }
  });
  return tabs;
};

export default {
  name: 'TabsCompat',

  props: {
    cacheLifetime: {
      default: 5,
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        useUrlFragment: true,
        defaultTabHash: null,
      }),
    },
  },

  data: () => ({
    tabs: [],
    activeTabHash: '',
    activeTabIndex: 0,
    lastActiveTabHash: '',
  }),

  computed: {
    storageKey() {
      return `vue-tabs-component.cache.${window.location.host}${window.location.pathname}`;
    },
  },

  mounted() {
    window.addEventListener('hashchange', this.onHashChange);
    this.$nextTick(() => {
      this.refreshTabs();
      this.selectInitialTab();
    });
  },

  beforeUnmount() {
    window.removeEventListener('hashchange', this.onHashChange);
  },

  methods: {
    refreshTabs() {
      if (Array.isArray(this.$children) && this.$children.length) {
        this.tabs = this.$children;
        return;
      }
      const slotTabs = collectTabs(this.$slots.default ? this.$slots.default() : []);
      this.tabs = slotTabs;
    },

    onHashChange() {
      this.selectTab(window.location.hash);
    },

    selectInitialTab() {
      if (this.findTab(window.location.hash)) {
        this.selectTab(window.location.hash);
        return;
      }

      const previousSelectedTabHash = expiringStorage.get(this.storageKey);
      if (this.findTab(previousSelectedTabHash)) {
        this.selectTab(previousSelectedTabHash);
        return;
      }

      if (this.options.defaultTabHash !== null && this.findTab(`#${this.options.defaultTabHash}`)) {
        this.selectTab(`#${this.options.defaultTabHash}`);
        return;
      }

      if (this.tabs.length) {
        this.selectTab(this.tabs[0].hash);
      }
    },

    findTab(hash) {
      return this.tabs.find(tab => tab.hash === hash);
    },

    selectTab(selectedTabHash, event) {
      if (event && !this.options.useUrlFragment) {
        event.preventDefault();
      }

      const selectedTab = this.findTab(selectedTabHash);

      if (!selectedTab) {
        return;
      }

      if (selectedTab.isDisabled) {
        if (event) {
          event.preventDefault();
        }
        return;
      }

      if (this.lastActiveTabHash === selectedTab.hash) {
        this.$emit('clicked', { tab: selectedTab });
        return;
      }

      this.tabs.forEach((tab) => {
        tab.isActive = (tab.hash === selectedTab.hash);
      });

      this.$emit('changed', { tab: selectedTab });

      this.activeTabHash = selectedTab.hash;
      this.activeTabIndex = this.getTabIndex(selectedTabHash);

      this.lastActiveTabHash = this.activeTabHash = selectedTab.hash;

      expiringStorage.set(this.storageKey, selectedTab.hash, this.cacheLifetime);
    },

    setTabVisible(hash, visible) {
      const tab = this.findTab(hash);

      if (!tab) {
        return;
      }

      tab.isVisible = visible;

      if (tab.isActive) {
        tab.isActive = visible;

        this.tabs.every((tabItem) => {
          if (tabItem.isVisible) {
            tabItem.isActive = true;
            return false;
          }

          return true;
        });
      }
    },

    getTabIndex(hash) {
      const tab = this.findTab(hash);
      return this.tabs.indexOf(tab);
    },

    getTabHash(index) {
      const tab = this.tabs.find(tabItem => this.tabs.indexOf(tabItem) === index);
      if (!tab) {
        return;
      }
      return tab.hash;
    },

    getActiveTab() {
      return this.findTab(this.activeTabHash);
    },

    getActiveTabIndex() {
      return this.getTabIndex(this.activeTabHash);
    },
  },
};
</script>
